import { oc } from '@orpc/contract';
import * as z from 'zod';
import { familySchema } from '../domain/family.js';
import { userIdSchema } from '../primitives/id.js';
import { instantSchema, timeZoneSchema } from '../primitives/time.js';
import { API_VERSION } from './version.js';
const base = `/${API_VERSION}/auth`;
/**
 * Minimum eight characters, and nothing else.
 *
 * Composition rules push people towards `Passw0rd!` and are no longer
 * recommended. Length plus a slow hash is what actually helps.
 */
export const passwordSchema = z.string().min(8).max(200);
/**
 * What a signed-in caller holds.
 *
 * The access token only. The refresh token travels as an httpOnly cookie and
 * never appears in a response body, so no script can read it — including one
 * injected into this application.
 *
 * That matters more here than in most products: these credentials control a
 * child's device. Somewhere to keep a long-lived token that JavaScript can read
 * does not exist, so the browser is asked to hold it instead.
 *
 * The access token stays in memory. It expires in minutes, and losing it on a
 * page reload costs one silent refresh.
 */
export const sessionSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: instantSchema,
});
/**
 * The cookie carrying the refresh token.
 *
 * Named here because the backend sets it and the parent application must send
 * it; a name agreed in two places is a name that eventually differs in two
 * places.
 *
 * `SameSite=Strict` is what defends the refresh endpoint from cross-site use,
 * which is why no separate CSRF token is required for it. It also imposes a
 * deployment constraint worth stating: the parent application and the API must
 * share a registrable domain (`app.example.com` and `api.example.com`), or the
 * browser will not send the cookie at all.
 */
export const REFRESH_COOKIE_NAME = 'kidshub_refresh';
export const REFRESH_COOKIE_PATH = '/v1/auth';
export const currentUserSchema = z.object({
    id: userIdSchema,
    email: z.email(),
    displayName: z.string(),
    family: familySchema,
});
export const authContract = {
    /**
     * Creates the account and its family together.
     *
     * A parent without a family cannot do anything in this product, so the two are
     * one step rather than a state the interface has to explain.
     */
    register: oc
        .route({ method: 'POST', path: `${base}/register` })
        .input(z.object({
        email: z.email(),
        password: passwordSchema,
        displayName: z.string().min(1).max(80),
        familyName: z.string().min(1).max(80),
        timeZone: timeZoneSchema,
    }))
        .output(z.object({ session: sessionSchema, user: currentUserSchema }))
        .errors({
        EMAIL_ALREADY_REGISTERED: { status: 409, message: 'Email is already registered' },
    }),
    login: oc
        .route({ method: 'POST', path: `${base}/login` })
        .input(z.object({ email: z.email(), password: passwordSchema }))
        .output(z.object({ session: sessionSchema, user: currentUserSchema }))
        .errors({
        // One error for both causes. Telling a caller which of the two failed
        // turns the endpoint into a way to enumerate registered addresses.
        INVALID_CREDENTIALS: { status: 401, message: 'Email or password is incorrect' },
    }),
    /**
     * Exchanges the refresh cookie for a new pair, retiring the old one.
     *
     * Takes no body: the token is in the cookie, and asking the caller to send it
     * as well would put it back within reach of a script.
     *
     * A token presented twice means the chain has leaked. The correct response is
     * to revoke the whole family of tokens rather than serve the request.
     */
    refresh: oc
        .route({ method: 'POST', path: `${base}/refresh` })
        .input(z.object({}))
        .output(z.object({ session: sessionSchema }))
        .errors({
        INVALID_REFRESH_TOKEN: {
            status: 401,
            message: 'Refresh token is invalid, expired or already used',
        },
    }),
    logout: oc
        .route({ method: 'POST', path: `${base}/logout` })
        .input(z.object({}))
        .output(z.object({ success: z.literal(true) })),
    me: oc.route({ method: 'GET', path: `${base}/me` }).output(currentUserSchema),
};
//# sourceMappingURL=auth.js.map